import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Paper,
    Typography,
    Button,
    IconButton,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Divider,
    Toolbar,
    makeStyles
} from "@material-ui/core";
import {
    ArrowBack as ArrowBackIcon,
    Reply as ReplyIcon,
    Forward as ForwardIcon,
    Delete as DeleteIcon,
    GetApp as GetAppIcon,
    AttachFile as AttachFileIcon
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import EmailViewer from "../../components/EmailViewer";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(3),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    emailHeader: {
        marginBottom: theme.spacing(3),
    },
    toolbar: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: theme.spacing(2),
    },
    subject: {
        fontSize: "1.5rem",
        fontWeight: "bold",
        marginBottom: theme.spacing(2),
    },
    metadata: {
        marginBottom: theme.spacing(1),
    },
    attachmentsList: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "400px",
    },
}));

const EmailRead = () => {
    const classes = useStyles();
    const history = useHistory();
    const { smtpConfigId, folder, uid } = useParams();

    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState(null);

    useEffect(() => {
        fetchEmail();
    }, [smtpConfigId, folder, uid]);

    const fetchEmail = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/emails/${smtpConfigId}/${folder}/${uid}`);
            setEmail(data);

            // Marcar como lido
            if (data.flags && !data.flags.includes("\\Seen")) {
                await api.put(`/emails/${smtpConfigId}/${folder}/${uid}/mark`, {
                    action: "read"
                });
            }
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        history.push("/emails");
    };

    const handleReply = () => {
        history.push("/emails/compose", {
            replyTo: email,
            smtpConfigId,
            folder,
            uid
        });
    };

    const handleForward = () => {
        history.push("/emails/compose", {
            forward: email,
            smtpConfigId
        });
    };

    const handleDelete = async () => {
        if (!window.confirm("Tem certeza que deseja deletar este e-mail?")) {
            return;
        }

        try {
            await api.put(`/emails/${smtpConfigId}/${folder}/${uid}/mark`, {
                action: "delete"
            });
            toast.success("E-mail deletado com sucesso");
            history.push("/emails");
        } catch (err) {
            toastError(err);
        }
    };

    const handleDownloadAttachment = async (attachmentIndex, filename) => {
        try {
            const response = await api.get(
                `/emails/${smtpConfigId}/${folder}/${uid}/attachment/${attachmentIndex}`,
                { responseType: "blob" }
            );

            // Criar link de download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success("Anexo baixado com sucesso");
        } catch (err) {
            toastError(err);
        }
    };

    const formatDate = (date) => {
        try {
            const parsedDate = typeof date === "string" ? parseISO(date) : date;
            return format(parsedDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
        } catch (error) {
            return "";
        }
    };

    if (loading) {
        return (
            <MainContainer>
                <MainHeader>
                    <Title>E-mail</Title>
                </MainHeader>
                <Paper className={classes.mainPaper} variant="outlined">
                    <div className={classes.loadingContainer}>
                        <CircularProgress />
                    </div>
                </Paper>
            </MainContainer>
        );
    }

    if (!email) {
        return (
            <MainContainer>
                <MainHeader>
                    <Title>E-mail</Title>
                </MainHeader>
                <Paper className={classes.mainPaper} variant="outlined">
                    <Typography>E-mail não encontrado</Typography>
                </Paper>
            </MainContainer>
        );
    }

    return (
        <MainContainer>
            <MainHeader>
                <Title>E-mail</Title>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <Toolbar className={classes.toolbar}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                    >
                        Voltar
                    </Button>
                    <div>
                        <IconButton onClick={handleReply} title="Responder">
                            <ReplyIcon />
                        </IconButton>
                        <IconButton onClick={handleForward} title="Encaminhar">
                            <ForwardIcon />
                        </IconButton>
                        <IconButton onClick={handleDelete} title="Deletar" color="secondary">
                            <DeleteIcon />
                        </IconButton>
                    </div>
                </Toolbar>

                <div className={classes.emailHeader}>
                    <Typography className={classes.subject}>
                        {email.subject}
                    </Typography>

                    <Typography variant="body2" className={classes.metadata}>
                        <strong>De:</strong> {email.from}
                    </Typography>

                    <Typography variant="body2" className={classes.metadata}>
                        <strong>Para:</strong> {email.to}
                    </Typography>

                    {email.cc && (
                        <Typography variant="body2" className={classes.metadata}>
                            <strong>Cc:</strong> {email.cc}
                        </Typography>
                    )}

                    <Typography variant="body2" className={classes.metadata} color="textSecondary">
                        {formatDate(email.date)}
                    </Typography>
                </div>

                {email.attachments && email.attachments.length > 0 && (
                    <>
                        <Divider />
                        <div className={classes.attachmentsList}>
                            <Typography variant="subtitle2" gutterBottom>
                                Anexos ({email.attachments.length})
                            </Typography>
                            <List dense>
                                {email.attachments.map((attachment, index) => (
                                    <ListItem
                                        key={index}
                                        button
                                        onClick={() => handleDownloadAttachment(index, attachment.filename)}
                                    >
                                        <ListItemIcon>
                                            <AttachFileIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={attachment.filename}
                                            secondary={`${(attachment.size / 1024).toFixed(2)} KB`}
                                        />
                                        <GetAppIcon />
                                    </ListItem>
                                ))}
                            </List>
                        </div>
                        <Divider />
                    </>
                )}

                <div style={{ marginTop: 16 }}>
                    <EmailViewer
                        htmlBody={email.htmlBody}
                        textBody={email.textBody}
                    />
                </div>
            </Paper>
        </MainContainer>
    );
};

export default EmailRead;
